import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router'

import { Button } from '@/components/elements/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/elements/select'
import { Textarea } from '@/components/elements/textarea'
import { FileUploader } from '@/components/FileUploader'
import { Modal } from '@/components/Modal'
import { MultiSelect } from '@/components/MultiSelect'
import { getCourses } from '@/services/courses.service'
import {
  createUserExternalCertification,
  getExternalCertificationCategories,
  updateUserExternalCertification,
} from '@/services/external_certifications.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

const SubmitCertificationModal = observer(({ setOpen, open, editingCertification }) => {
  const { entityId } = useParams()
  const { activeEntity } = useContext(UserStoreContext)
  const queryClient = useQueryClient()

  const defaultValues = editingCertification
    ? {
        category: editingCertification.category?.id || '',
        completionDate: editingCertification.completionDate || '',
        expiryDate: editingCertification.expiryDate || '',
        notes: editingCertification.notes || '',
        courses: editingCertification.courses?.map((c) => c.id || c) || [],
        files: editingCertification.files || [],
      }
    : {
        category: '',
        completionDate: '',
        expiryDate: '',
        notes: '',
        courses: [],
        files: [],
      }

  const form = useForm({ defaultValues })
  const files = useWatch({ name: 'files', control: form.control })

  const resetForm = () => {
    form.reset(defaultValues)
    setOpen(false)
  }

  // Load certification categories
  const { data: categoriesData } = useQuery({
    queryKey: ['external-certification-categories', entityId],
    queryFn: () =>
      getExternalCertificationCategories({
        entityId: entityId || activeEntity,
        filters: { is_active: true },
      }),
    enabled: open,
  })

  const categories = categoriesData?.results || []

  // Load available courses
  const { data: coursesData } = useQuery({
    queryKey: ['courses-list'],
    queryFn: () => getCourses(null, null, null, null, true),
    enabled: open,
  })

  const courseOptions = (coursesData?.results || []).map((course) => ({
    id: course.id,
    label: course.name,
  }))

  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')

  const { isPending, mutate: saveCertification } = useMutation({
    mutationFn: async (data) => {
      const payload = {
        category: data.category,
        entity: entityId || activeEntity,
        completionDate: data.completionDate,
        expiryDate: data.expiryDate || null,
        notes: data.notes,
        courses: data.courses || [],
        files: data.files.map((f) => ({
          filePath: f.filePath,
          fileName: f.fileName,
        })),
      }

      if (editingCertification) {
        return updateUserExternalCertification(
          entityId || activeEntity,
          editingCertification.id,
          payload,
        )
      }
      return createUserExternalCertification(entityId || activeEntity, payload)
    },
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      resetForm()
      handleSuccess(
        editingCertification
          ? 'Credential updated successfully'
          : 'Credential submitted for review',
      )
      queryClient.invalidateQueries({ queryKey: ['external-certifications'] })
    },
  })

  const onSubmit = (data) => saveCertification(data)

  const addFile = (fileInfo) => {
    const currentFiles = form.getValues('files') || []
    form.setValue('files', [
      ...currentFiles,
      { filePath: fileInfo.fileUploadName, fileName: fileInfo.fileName },
    ])
  }

  const removeFile = (index) => {
    const currentFiles = form.getValues('files') || []
    form.setValue(
      'files',
      currentFiles.filter((_, i) => i !== index),
    )
  }

  return (
    <Modal
      open={open}
      title={editingCertification ? 'Edit Credential' : 'Submit External Credential'}
      content={
        <Form {...form}>
          <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              disabled={isPending}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Credential Category *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value ? String(field.value) : ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={String(cat.id)}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: 'Please select a category' }}
            />

            <FormField
              control={form.control}
              disabled={isPending}
              name="completionDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Completion Date *</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: 'Completion date is required' }}
            />

            <FormField
              control={form.control}
              disabled={isPending}
              name="expiryDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Expiry Date (Optional)</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              disabled={isPending}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add any additional notes about this credential"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              disabled={isPending}
              name="courses"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Equivalent Courses (Optional)</FormLabel>
                  <FormControl>
                    <MultiSelect
                      options={courseOptions}
                      value={field.value || []}
                      onChange={field.onChange}
                      placeholder="Select courses..."
                      width="w-full"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="files"
              rules={{
                validate: (value) =>
                  (value && value.length > 0) || 'At least one supporting document is required',
              }}
              render={() => (
                <FormItem>
                  <FormLabel>Supporting Documents *</FormLabel>
                  {files && files.length > 0 && (
                    <div className="mb-2 flex flex-col gap-2">
                      {files.map((file, fileIndex) => (
                        <div
                          key={file.filePath || file.id}
                          className="flex items-center justify-between rounded border p-2"
                        >
                          <span className="truncate text-sm">
                            {file.fileName || file.file_name}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(fileIndex)}
                            disabled={isPending}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <FileUploader
                    filePrefix="external_certifications"
                    handleUploadToServer={addFile}
                    disabled={isPending}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="mt-8 flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={resetForm}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {editingCertification ? 'Update' : 'Submit for Review'}
              </Button>
            </div>
          </form>
        </Form>
      }
      setOpen={setOpen}
    />
  )
})

export default SubmitCertificationModal
